import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeatureContext';
import { 
  LayoutDashboard, Users, Wallet, Truck, ShoppingBag, MessageCircle, 
  TrendingUp, LifeBuoy, FileText, Settings, ChevronDown, ChevronRight,
  Search, Menu, X, CreditCard, Coins, TrendingDown, TrendingUpIcon,
  Building, ScrollText, Gift, UserCheck, Share2, Database, Network,
  CircleDollarSign, Receipt, PiggyBank, Package, ShoppingCart, 
  MessageSquare, Target, BarChart3, Megaphone, Ticket, Shield,
  FileEdit, ToggleLeft, Archive, Users2, Activity
} from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

/**
 * AdminSidebar - Reorganized admin navigation with collapsible sections
 * Features: Role-based visibility, feature toggle integration, search, mobile support
 */
export function AdminSidebar({ isMobileOpen, onMobileClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const { isFeatureEnabled } = useFeatures();
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Section icons mapping
  const sectionIcons = {
    dashboard: LayoutDashboard,
    users: Users,
    money: Wallet,
    runners: Truck,
    marketplace: ShoppingBag,
    community: MessageCircle,
    ads: TrendingUp,
    support: LifeBuoy,
    content: FileText,
    settings: Settings
  };

  // Define all admin sections and pages
  const adminStructure = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin/dashboard',
      roles: ['super_admin', 'admin', 'finance_admin', 'marketplace_admin', 'support']
    },
    {
      id: 'users',
      title: 'Users',
      icon: Users,
      roles: ['super_admin', 'admin'],
      subsections: [
        { title: 'All Users', path: '/admin/users', icon: Users2 },
        { title: 'User Groups', path: '/admin/groups', icon: Users },
        { title: 'Agents', path: '/admin/agents', icon: UserCheck },
        { title: 'Agent Dashboard', path: '/admin/agents/dashboard', icon: Activity },
        { title: 'Referrals', path: '/admin/referrals', icon: Share2 }
      ]
    },
    {
      id: 'money',
      title: 'Money',
      icon: Wallet,
      roles: ['super_admin', 'admin', 'finance_admin'],
      subsections: [
        {
          group: 'Wallets & Balances',
          items: [
            { title: 'User Wallets', path: '/admin/wallets', icon: Wallet },
            { title: 'Custodial Wallets', path: '/admin/custodial-wallets', icon: Database },
            { title: 'Blockchain Wallets', path: '/admin/blockchain-wallets', icon: Network },
            { title: 'Treasury Wallets', path: '/admin/treasury/wallets', icon: Building }
          ]
        },
        {
          group: 'Currencies & Exchange',
          items: [
            { title: 'Currencies', path: '/admin/currencies', icon: Coins },
            { title: 'Exchange Rates', path: '/admin/exchange-rates', icon: TrendingUpIcon },
            { title: 'Exchange Management', path: '/admin/exchange-management', icon: CircleDollarSign },
            { title: 'Exchange Dashboard', path: '/admin/exchange-dashboard', icon: BarChart3 }
          ]
        },
        {
          group: 'Deposits & Withdrawals',
          items: [
            { title: 'Deposits', path: '/admin/deposits', icon: TrendingUp, badge: true },
            { title: 'Deposit Methods', path: '/admin/deposit-methods', icon: CreditCard },
            { title: 'Withdrawals', path: '/admin/withdrawals', icon: TrendingDown, badge: true },
            { title: 'Withdrawal Fees', path: '/admin/withdrawal-fees', icon: Receipt },
            { title: 'Auto Withdrawals', path: '/admin/auto-withdrawals', icon: Activity }
          ]
        },
        {
          group: 'Treasury & Blockchain',
          items: [
            { title: 'Treasury', path: '/admin/treasury', icon: Building },
            { title: 'Blockchain Assets', path: '/admin/blockchain/assets', icon: Database },
            { title: 'Blockchain Networks', path: '/admin/blockchain-networks', icon: Network },
            { title: 'Asset Consolidation', path: '/admin/consolidation', icon: Archive }
          ]
        },
        {
          group: 'Transactions & Reports',
          items: [
            { title: 'Transactions', path: '/admin/transactions', icon: ScrollText },
            { title: 'Vouchers', path: '/admin/vouchers', icon: Ticket },
            { title: 'Savings', path: '/admin/savings', icon: PiggyBank }
          ]
        }
      ]
    },
    {
      id: 'runners',
      title: 'Runners',
      icon: Truck,
      roles: ['super_admin', 'admin'],
      feature: 'runners_service',
      subsections: [
        { title: 'Runners Dashboard', path: '/admin/pickup', icon: LayoutDashboard },
        { title: 'Disputes', path: '/admin/disputes', icon: Shield, badge: true }
      ]
    },
    {
      id: 'marketplace',
      title: 'Marketplace',
      icon: ShoppingBag,
      roles: ['super_admin', 'admin', 'marketplace_admin'],
      feature: 'marketplace',
      subsections: [
        { title: 'Marketplace Overview', path: '/admin/marketplace', icon: ShoppingBag },
        { title: 'Commerce Settings', path: '/admin/commerce', icon: Settings },
        { title: 'Shop Management', path: '/admin/shop', icon: ShoppingCart },
        { title: 'Orders', path: '/admin/orders', icon: Package }
      ]
    },
    {
      id: 'community',
      title: 'Community',
      icon: MessageCircle,
      roles: ['super_admin', 'admin'],
      feature: 'connect_feed',
      subsections: [
        { title: 'Matrise Connect', path: '/admin/connect', icon: MessageCircle },
        { title: 'Wants & Offers', path: '/admin/wants-offers', icon: MessageSquare },
        { title: 'Tasking', path: '/admin/tasking', icon: Target },
        { title: 'Predictions', path: '/admin/predictions', icon: TrendingUp },
        { title: 'Pool Predictions', path: '/admin/pool-predictions', icon: Activity }
      ]
    },
    {
      id: 'ads',
      title: 'Ads and Growth',
      icon: TrendingUp,
      roles: ['super_admin', 'admin'],
      feature: 'ads_platform',
      subsections: [
        {
          group: 'Advertising',
          items: [
            { title: 'Ads Management', path: '/admin/marketing/ads', icon: Megaphone },
            { title: 'Ads Analytics', path: '/admin/marketing/analytics', icon: BarChart3 },
            { title: 'Ads Settings', path: '/admin/marketing/settings', icon: Settings },
            { title: 'Auto Ads', path: '/admin/auto-ads', icon: Activity }
          ]
        },
        {
          group: 'Growth & Promotions',
          items: [
            { title: 'System Promotions', path: '/admin/marketing/promotions', icon: Gift },
            { title: 'Growth Packages', path: '/admin/growth-packages', icon: TrendingUp }
          ]
        }
      ]
    },
    {
      id: 'support',
      title: 'Support',
      icon: LifeBuoy,
      roles: ['super_admin', 'admin', 'support'],
      subsections: [
        { title: 'Support Tickets', path: '/admin/support', icon: LifeBuoy, badge: true },
        { title: 'Audit Logs', path: '/admin/audit-logs', icon: ScrollText }
      ]
    },
    {
      id: 'content',
      title: 'Content and Branding',
      icon: FileText,
      roles: ['super_admin', 'admin'],
      subsections: [
        { title: 'Landing Page Editor', path: '/admin/landing', icon: FileEdit }
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Settings,
      roles: ['super_admin', 'admin'],
      subsections: [
        { title: 'General Settings', path: '/admin/settings', icon: Settings },
        { title: 'Feature Controls', path: '/admin/feature-controls', icon: ToggleLeft, roles: ['super_admin'] },
        { title: 'Backup & Restore', path: '/admin/backup', icon: Archive, roles: ['super_admin'] }
      ]
    }
  ];

  // Filter sections by role and feature toggles
  const visibleSections = useMemo(() => {
    const userRole = user?.role || 'admin';
    
    return adminStructure.filter(section => {
      // Check role access
      if (!section.roles.includes(userRole)) return false;
      
      // Check feature toggle
      if (section.feature && !isFeatureEnabled(section.feature)) return false;
      
      return true;
    }).map(section => {
      // Filter subsections by role if needed
      if (section.subsections) {
        const filteredSubs = section.subsections
          .map(sub => {
            if (sub.group) {
              // It's a grouped subsection (like Money)
              return {
                ...sub,
                items: sub.items.filter(item => 
                  !item.roles || item.roles.includes(userRole)
                )
              };
            }
            // Regular subsection item
            return (!sub.roles || sub.roles.includes(userRole)) ? sub : null;
          })
          .filter(Boolean);
        
        return { ...section, subsections: filteredSubs };
      }
      
      return section;
    });
  }, [user, isFeatureEnabled]);

  // Search functionality
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    const results = [];
    
    visibleSections.forEach(section => {
      // Search in section title
      if (section.title.toLowerCase().includes(query)) {
        results.push({ ...section, type: 'section' });
      }
      
      // Search in subsections
      if (section.subsections) {
        section.subsections.forEach(sub => {
          if (sub.group) {
            // Grouped subsection
            sub.items.forEach(item => {
              if (item.title.toLowerCase().includes(query)) {
                results.push({ ...item, sectionTitle: section.title, type: 'page' });
              }
            });
          } else if (sub.title.toLowerCase().includes(query)) {
            results.push({ ...sub, sectionTitle: section.title, type: 'page' });
          }
        });
      }
    });
    
    return results;
  }, [searchQuery, visibleSections]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const isActive = (path) => location.pathname === path;

  const handleLinkClick = () => {
    if (onMobileClose) onMobileClose();
  };

  // Render page item
  const renderPageItem = (item, sectionId) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={handleLinkClick}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
          active 
            ? 'bg-primary text-primary-foreground' 
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        }`}
      >
        {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
        <span className="flex-1">{item.title}</span>
        {item.badge && <Badge variant="destructive" className="h-5 px-1.5 text-xs">!</Badge>}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-background border-r border-border
        transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        overflow-y-auto
      `}>
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border p-4 space-y-3 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Admin Panel</h2>
            <button
              onClick={onMobileClose}
              className="lg:hidden p-1 hover:bg-accent rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Search Box */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search pages... (Ctrl+K)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
        </div>

        {/* Search Results */}
        {searchQuery && (
          <div className="p-2 border-b border-border">
            <p className="text-xs text-muted-foreground px-3 py-2">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
            </p>
            {searchResults.map((result, idx) => (
              <div key={idx}>
                {result.type === 'page' ? (
                  <Link
                    to={result.path}
                    onClick={handleLinkClick}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-accent"
                  >
                    {result.icon && <result.icon className="h-4 w-4" />}
                    <div className="flex-1">
                      <div>{result.title}</div>
                      <div className="text-xs text-muted-foreground">{result.sectionTitle}</div>
                    </div>
                  </Link>
                ) : (
                  <div className="px-3 py-2 text-sm font-medium text-foreground">
                    {result.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Navigation */}
        <nav className="p-2 space-y-1">
          {visibleSections.map(section => {
            const SectionIcon = section.icon;
            const isExpanded = expandedSections[section.id];
            const hasSubsections = section.subsections && section.subsections.length > 0;
            
            return (
              <div key={section.id}>
                {/* Section Header */}
                {hasSubsections ? (
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium hover:bg-accent transition-colors"
                  >
                    <SectionIcon className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 text-left">{section.title}</span>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                ) : (
                  <Link
                    to={section.path}
                    onClick={handleLinkClick}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive(section.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    }`}
                  >
                    <SectionIcon className="h-4 w-4 flex-shrink-0" />
                    <span>{section.title}</span>
                  </Link>
                )}

                {/* Subsections */}
                {hasSubsections && isExpanded && (
                  <div className="ml-6 mt-1 space-y-0.5 border-l border-border pl-2">
                    {section.subsections.map((sub, idx) => {
                      if (sub.group) {
                        // Grouped subsection (like Money subsections)
                        return (
                          <div key={idx} className="mt-2">
                            <div className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase">
                              {sub.group}
                            </div>
                            {sub.items.map(item => renderPageItem(item, section.id))}
                          </div>
                        );
                      }
                      // Regular subsection item
                      return renderPageItem(sub, section.id);
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
